import { isUndefined } from 'lodash-es';
import { type KeyboardEvent } from 'react';

import { TimeseriesSelectionAreaTooltip } from './TimeseriesSelectionAreaTooltip.js';
import { TimeseriesSelectionDatapoints } from './TimeseriesSelectionDatapoints.js';
import { SelectionArea } from '../../../../core/components/selection-area/SelectionArea.js';
import { useSynchronizedSelection } from '../../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import type { RelativeAbsolutePosition } from '../../../../core/types/pointer-events.js';
import {
  useInteractionModeState,
  useSelectState,
  useSetState,
} from '../../../hooks/store/selectors.js';
import { useZoomToSelection } from '../../../hooks/store/state-setters.js';
import { useChartInteractions } from '../../../hooks/useChartInteractions.js';
import { useTimeseriesChartId } from '../../../hooks/useTimeseriesChartId.js';
import { useTimeseriesOverlayRef } from '../../../hooks/useTimeseriesOverlayRef.js';
import { useTimeseriesSelectionAreaRef } from '../../../hooks/useTimeseriesSelectionAreaRef.js';
import { useTimeseriesXFormatter } from '../../../hooks/useTimeseriesXFormatter.js';
import { useXScales } from '../../../hooks/useXScale.js';
import { reduceExploreTimeseriesSelect } from '../../../utils/state-reducers.js';

export const TimeseriesSelectionArea = () => {
  const bottomFormatter = useTimeseriesXFormatter();
  const selectionAreaRef = useTimeseriesSelectionAreaRef();
  const { currentScale: xScale } = useXScales();
  const chartRef = useTimeseriesOverlayRef();
  const chartBounds = chartRef.current?.getBoundingClientRect();
  const setState = useSetState();
  const zoomToSelection = useZoomToSelection();
  const selectState = useSelectState();
  const { zoomX } = useChartInteractions();

  const interactionMode = useInteractionModeState();

  const { updateSelectionState } = useSynchronizedSelection();
  const chartId = useTimeseriesChartId();

  if (isUndefined(selectState) || isUndefined(chartBounds)) {
    return null;
  }

  const { start, end, finished, accessible, variant } = selectState;

  const handleWrapperKeyDown = (event: KeyboardEvent) => {
    const canZoom = zoomX?.enabled;
    if (event.key === 'Enter' && canZoom) {
      zoomToSelection();
      chartRef.current?.focus();
    }
  };

  const handleSelectionChange = ({
    selection,
    accessible,
  }: {
    selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
    accessible: boolean;
  }) => {
    setState(
      reduceExploreTimeseriesSelect({
        start: selection[0],
        end: selection[1],
        finished: true,
        accessible,
        variant,
        domainStart: xScale.invert(selection[0].relative.x),
        domainEnd: xScale.invert(selection[1].relative.x),
      }),
    );
    if (interactionMode === 'explore') {
      updateSelectionState?.({
        selection,
        chartId,
        scale: xScale,
      });
    }
  };

  const getPosition = (
    position: RelativeAbsolutePosition,
    domainKey: 'domainStart' | 'domainEnd',
  ) =>
    finished
      ? {
          ...position,
          relative: { ...position.relative, x: xScale(selectState[domainKey]) },
        }
      : position;

  const startPos = getPosition(start, 'domainStart');
  const endPos = getPosition(end, 'domainEnd');

  return (
    <>
      {variant === 'select' ? (
        <>
          <TimeseriesSelectionDatapoints />
          <TimeseriesSelectionAreaTooltip />
        </>
      ) : null}

      <SelectionArea
        selection={[startPos, endPos]}
        bounds={chartBounds}
        variant={variant}
        focusable={accessible}
        interactable={finished}
        bottomFormatter={(value) => bottomFormatter(xScale.invert(value))}
        onKeyDown={handleWrapperKeyDown}
        onSelectionChange={handleSelectionChange}
        ref={selectionAreaRef}
      />
    </>
  );
};
